import request from '@/utilities/request';
import { hashData } from '@/utilities/index';
import { sendMessageToSentry } from '@/utilities/sentry.js';
import { loadRecaptchaScript, initializeScoreBasedRecaptcha } from '@/utilities/recaptcha-enterprise.js';

export default {
  async loadRecaptcha({ commit, state }) {
    if (!state.recaptcha) {
      try {
        // Load reCAPTCHA Enterprise script
        await loadRecaptchaScript();
        
        // Initialize score-based reCAPTCHA
        const recaptcha = initializeScoreBasedRecaptcha();
        commit('setRecaptcha', recaptcha);
      } catch (e) {
        sendMessageToSentry('RecaptchaLoadFail', {
          level: 'error',
          extra: [
            {
              name: 'error',
              value: e?.message || e?.response,
            },
          ],
        });
      }
    }
  },
  async getSession({ commit, dispatch, state, rootState }) {
    if (!state.hasCheckSession) {
      commit('setIsLoading', true);

      const res = await request.get('/signin/module.php/tmauth/getnameid');

      // mini browser header
      if (res.data.product_name) {
        commit(
          'setMiniBrowserHeaderInfo',
          {
            productName: res.data.product_name,
          },
          { root: true },
        );
      }
      if (res.data.caid) {
        const caid = res.data.caid.split('.')[0];
        commit('setCaid', caid, { root: true });
      }

      if (res.data.support_auth_method) {
        commit('setSupportAuthMethod', res.data.support_auth_method);

        const isPasswordSet = res.data.support_auth_method.includes('password');
        commit('setIsPasswordSet', isPasswordSet);
      }

      if (res.data.auth_with_hint) {
        commit('setIsAccountInputFixed', res.data.auth_with_hint === '1');
      }

      if (res.data.sms) {
        commit('setPrefilledSmsAccount', res.data);
      } else if (res.data.email) {
        commit('setPrefilledEmailAccount', {
          email: res.data.email,
        });
      }

      if (res.data.social_google_email || res.data.social_apple_email || res.data.social_line_email) {
        let valueReplaceMapping = {
          '{firstName}': res.data.first_name,
          '{lastName}': res.data.last_name,
        };

        const username = rootState.lang.nameDisplay.replace(
          /{firstName}|{lastName}/gi,
          (matched) => valueReplaceMapping[matched],
        );

        commit('setPrefilledSocialInfo', {
          social_google_email: res.data.social_google_email,
          social_apple_email: res.data.social_apple_email,
          social_line_email: res.data.social_line_email,
          username,
        });
      }

      if (res.data.sn) {
        commit('setSn', res.data.sn[0], { root: true });

        const hashedSN = await Promise.all(
          res.data.sn.map(async (sn) => hashData(sn, 'SHA-1').then((i) => i)),
        );

        commit('setHashedSN', hashedSN, { root: true });
      }

      if (res.data.allow_action) {
        commit('setAllowAction', res.data.allow_action);
      }

      if (res.data.account) {
        commit('setSignUpFormColumn', res.data.account);
      }

      if (res.data.skip_email_verification) {
        commit('setSkipEmailVerification', res.data.skip_email_verification);
      }

      if (res.data.disable_email_verification) {
        commit('setDisableEmailVerification', res.data.disable_email_verification);
      }

      if (res.data.show_old_sso_ui !== undefined) {
        commit('setShowOldSsoUi', res.data.show_old_sso_ui);
      }

      if (res.data.ui_mode) {
        commit('setUiMode', res.data.ui_mode);
      }

      if (res.data.support_line_oauth !== undefined) { // Current JP=true, else=false
        commit('setSupportLineOauth', res.data.support_line_oauth);
      }

      if (res.data.support_inapp_polling !== undefined) {
        commit('setSupportInappPolling', res.data.support_inapp_polling);
      }

      let tclogInfo = { login_from: '' };

      const loginFrom = res.data.login_flow_id;
      if (loginFrom) {
        tclogInfo.login_from = loginFrom;
        commit('setLoginFrom', loginFrom, { root: true });
      }

      const clientId = res.data.client_id;
      if (clientId) {
        tclogInfo.client_id = clientId;
        commit('setClientId', clientId, { root: true });

        if (clientId.includes('IDP')) {
          if (process.env.CLARITY_PROJECT_ID) {
            import('@/utilities/m-clarity.js').then(({ initClarity }) => initClarity());
          }

          commit('setAllowLang', ['en-US', 'ja-JP', 'zh-TW', 'de-DE', 'fr-FR'], { root: true });
        }

        if (clientId.includes('TMC')) {
          commit('setAllowLang', ['en-US', 'ja-JP', 'zh-TW', 'de-DE', 'fr-FR'], { root: true });
        }
      }

      if (res.data.vendor) {
        commit('setVendor', res.data.vendor, { root: true })
        tclogInfo.vendor = res.data.vendor;
      }

      if (res.data.utm_source) {
        tclogInfo.utm_source = res.data.utm_source;
      }

      commit('setTcLogInfo', tclogInfo, { root: true });

      if (res.data.module_uuid) {
        const uuid = res.data.module_uuid;
        commit('setModuleUUID', uuid, { root: true });
      }

      if (res.data.lang) {
        const lang = `${res.data.lang.split('-')[0].toLowerCase()}-${res.data.lang.split('-')[1]}`;
        await dispatch(
          'initialLanguage',
          {
            mod: 'auth',
            specificLang: lang,
          },
          { root: true },
        );
      }
      else await dispatch('initialLanguage', { mod: 'auth' }, { root: true });

      commit('setHasCheckSession', true);
      commit('setIsLoading', false);
      commit('setIsInit', true, { root: true });
    } else {
      commit('setIsInit', true, { root: true });
    }
  },
  async refreshSession({ commit, dispatch }) {
    try {
      commit('setHasCheckSession', false);
      await dispatch('getSession');
    } catch (e) {
      // Swallow errors — the caller handles missing data gracefully
      commit('setIsLoading', false);
    }
  },
  async getIsoCountryCode({ commit, state }) {
    if (!state.isoCountryCode) {
      const res = await request.get('/signin/module.php/tmauth/getcountryisocode');
      commit('setIsoCountryCode', res.data);
    }
  },
};
